import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 backdrop-blur-md",
  {
    variants: {
      variant: {
        default: "gradient-cta-animated text-primary-foreground shadow-card hover:shadow-glow hover:scale-[1.02] border border-primary/20",
        destructive: "bg-gradient-to-r from-destructive to-destructive/80 text-destructive-foreground backdrop-blur-md border border-destructive/30 shadow-card hover:shadow-glow hover:scale-[1.02]",
        outline: "bg-card/40 backdrop-blur-xl border border-border/50 text-foreground hover:bg-card/60 hover:border-primary/40 hover:shadow-card",
        secondary: "bg-gradient-to-r from-secondary/80 to-secondary/60 backdrop-blur-xl text-secondary-foreground border border-border/30 hover:from-secondary/90 hover:to-secondary/70 hover:shadow-card",
        ghost: "hover:bg-accent/40 hover:backdrop-blur-xl hover:text-accent-foreground hover:border-border/20",
        link: "text-primary underline-offset-4 hover:underline backdrop-blur-none",
        // Equilybra custom variants
        whatsapp: "gradient-whatsapp-animated text-primary-foreground border border-[hsl(142_70%_50%/0.3)] shadow-card hover:shadow-glow hover:scale-[1.02]",
        call: "gradient-cta-animated text-primary-foreground border border-primary/20 shadow-card hover:shadow-glow hover:scale-[1.02]",
        hero: "gradient-cta-animated text-primary-foreground shadow-card hover:shadow-glow hover:scale-[1.03] font-semibold border border-primary/20",
        "hero-outline": "bg-card/30 backdrop-blur-xl border-2 border-primary/30 text-foreground hover:bg-primary/90 hover:text-primary-foreground hover:border-primary hover:shadow-glow hover:scale-[1.02]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-lg px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
